<script setup lang="ts">
/**
 * AgentAuthPanel — продающая левая панель публичных экранов агента (вход/регистрация/
 * восстановление и т.д.), Figma QLdiQ1 AD1–AD6. Фото ЖК + скрим для читаемости,
 * оффер и цитата основателя (DEV-1014, DEV-694). Копия — в i18n `agents.authPanel`.
 *
 * agents-hero.jpg — плейсхолдер, заменяется на финальный рендер ЖК от заказчика.
 */
import { useI18n } from 'vue-i18n'
import logoWhite from '@/assets/vinprop-agents-logo-white.svg'
import heroImg from '@/assets/agents-hero.jpg'

const { t } = useI18n()

const features = [
  { key: 'partnership', icon: 'shield' },
  { key: 'projects', icon: 'building' },
  { key: 'support', icon: 'support' },
] as const
</script>

<template>
  <aside class="auth-panel">
    <div class="auth-panel__bg" :style="{ backgroundImage: `url(${heroImg})` }" aria-hidden="true" />
    <div class="auth-panel__scrim" aria-hidden="true" />

    <div class="auth-panel__brand">
      <img :src="logoWhite" class="auth-panel__logo" alt="VINPROP Agents" />
      <span class="auth-panel__sub">{{ t('agents.portalTitle') }}</span>
    </div>

    <div class="auth-panel__body">
      <h1 class="auth-panel__title">{{ t('agents.authPanel.headline') }}</h1>
      <p class="auth-panel__lead">{{ t('agents.authPanel.subheadline') }}</p>

      <ul class="auth-panel__feats">
        <li v-for="f in features" :key="f.key" class="auth-feat">
          <span class="auth-feat__ic" aria-hidden="true">
            <svg
              v-if="f.icon === 'shield'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <svg
              v-else-if="f.icon === 'building'"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="4" y="2" width="16" height="20" rx="2" />
              <path
                d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01"
              />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
              <path
                d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"
              />
            </svg>
          </span>
          <div class="auth-feat__text">
            <p class="auth-feat__title">{{ t(`agents.authPanel.${f.key}Title`) }}</p>
            <p class="auth-feat__desc">{{ t(`agents.authPanel.${f.key}Desc`) }}</p>
          </div>
        </li>
      </ul>
    </div>

    <figure class="auth-panel__quote">
      <p>{{ t('agents.authPanel.quote') }}</p>
      <figcaption>{{ t('agents.authPanel.quoteAuthor') }}</figcaption>
    </figure>
  </aside>
</template>

<style scoped>
.auth-panel {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  padding: 44px 52px 40px;
  color: #fff;
}

.auth-panel__bg {
  position: absolute;
  inset: 0;
  z-index: -2;
  background-size: cover;
  background-position: center 42%;
}

.auth-panel__scrim {
  position: absolute;
  inset: 0;
  z-index: -1;
  /* Затемнение для читаемости белого текста на фото (Figma: base #1E1B4B .5 + вертикальный градиент). */
  background:
    linear-gradient(
      180deg,
      rgba(49, 46, 129, 0.36) 0%,
      rgba(30, 27, 75, 0.62) 50%,
      rgba(15, 23, 42, 0.9) 100%
    ),
    rgba(30, 27, 75, 0.34);
}

.auth-panel__brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.auth-panel__logo {
  height: 30px;
  width: auto;
  display: block;
}

.auth-panel__sub {
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.72);
  padding-left: 12px;
  border-left: 1px solid rgba(255, 255, 255, 0.24);
}

.auth-panel__body {
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.auth-panel__title {
  margin: 0;
  font-size: 2.125rem;
  line-height: 1.16;
  font-weight: 700;
  letter-spacing: -0.015em;
  text-wrap: balance;
  text-shadow: 0 1px 20px rgba(15, 23, 42, 0.25);
}

.auth-panel__lead {
  margin: 0;
  max-width: 48ch;
  font-size: 0.96875rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.86);
}

.auth-panel__feats {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.auth-feat {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.auth-feat__ic {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.28);
  display: grid;
  place-items: center;
  color: #fff;
}

.auth-feat__ic svg {
  width: 20px;
  height: 20px;
}

.auth-feat__title {
  margin: 2px 0 3px;
  font-size: 0.90625rem;
  font-weight: 600;
  line-height: 1.3;
}

.auth-feat__desc {
  margin: 0;
  max-width: 44ch;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.8);
}

.auth-panel__quote {
  margin: 0;
  max-width: 500px;
  padding: 18px 20px 16px;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.38);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(6px);
}

.auth-panel__quote p {
  margin: 0 0 8px;
  font-size: 0.90625rem;
  line-height: 1.5;
  font-weight: 500;
}

.auth-panel__quote figcaption {
  font-size: 0.78125rem;
  color: rgba(255, 255, 255, 0.74);
}

@media (max-width: 900px) {
  .auth-panel {
    min-height: auto;
    gap: 22px;
    padding: 32px 28px;
    justify-content: flex-start;
  }

  .auth-panel__title {
    font-size: 1.6875rem;
  }

  .auth-panel__feats {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px 22px;
  }

  .auth-feat {
    flex: 1 1 240px;
  }

  .auth-panel__quote {
    display: none;
  }
}

@media (max-width: 560px) {
  .auth-panel {
    padding: 26px 20px;
    gap: 18px;
  }

  .auth-panel__title {
    font-size: 1.4375rem;
  }

  .auth-panel__feats {
    flex-direction: column;
    gap: 14px;
  }

  .auth-feat {
    flex: 0 0 auto;
  }
}
</style>
